import type { TFunction } from 'i18next';
import { toJS } from 'mobx';

import type { GoogleAdsAdGroup, GoogleAdsSearchCreative } from '@feathr/blackbox';
import { MAX_SITELINKS_PER_AD_GROUP } from '@feathr/blackbox';

import { AD_TEXT_LIMITS } from '../GoogleAdsCampaignEdit.utils';
import type {
  IAd,
  IAdGroup,
  IAdGroupRowErrors,
  IAdRowErrors,
  ISitelink,
  ISitelinkRowErrors,
  IValidateAdStepResult,
} from './AdStep.types';

/**
 * Minimums the Google Ad Grants program enforces on Standard Search campaigns.
 * Falling short of any of them blocks publishing.
 *
 * @see https://support.google.com/grants/answer/9905775
 */
export const AD_GRANT_LIMITS = {
  minAdGroups: 2,
  minAdsPerAdGroup: 2,
  minKeywordsPerAdGroup: 1,
  minSitelinksPerAdGroup: 2,
} as const;

/**
 * One ad group in every form validation needs it: the view the step renders, the model
 * whose constraints cover the structural rules, and the creatives backing its ads.
 */
export interface IAdGroupValidationInput {
  view: IAdGroup;
  model: GoogleAdsAdGroup;
  ads: readonly GoogleAdsSearchCreative[];
}

/*
 * What the two models' constraints produce in grouped form. Declared rather than derived
 * from `TValidateGrouped`: a per-row validator leaves clean rows `undefined`, which that
 * type's index signature does not admit, and widening it reaches into `@feathr/hooks`.
 */

/** One entry per row for `keywords` and `sitelinks`, index-aligned to the model's lists. */
interface IAdGroupModelErrors {
  name?: string[];
  keywords?: Array<string | undefined>;
  sitelinks?: Array<Record<string, string[]> | undefined>;
}

interface IAdModelErrors {
  destination_url?: string[];
  headlines?: Array<string | undefined>;
  descriptions?: Array<string | undefined>;
}

/**
 * Single-word keywords are the too-generic case Google calls out explicitly. Not a model
 * constraint: `uniqueBy` has to stay the only per-row validator on `keywords` for its
 * errors to keep lining up, so this one rule lives here.
 *
 * @see https://support.google.com/grants/answer/9160607
 */
export function isKeywordTooGeneric(text: string): boolean {
  return text.trim().split(/\s+/).filter(Boolean).length <= 1;
}

/* Both helpers below feed `AdPreview`. */

/** Google shows the display URL without its scheme, e.g. `example.org/programs`. */
export function toDisplayUrl(url: string): string {
  return url.trim().replace(/^https?:\/\//i, '');
}

/** The labels of every sitelink that has one — the rest can't render in an ad. */
export function toSitelinkLabels(sitelinks: readonly ISitelink[]): string[] {
  return sitelinks.map((sitelink) => sitelink.text.trim()).filter(Boolean);
}

function countFilled(items: ReadonlyArray<{ readonly text: string }>): number {
  return items.filter((item) => !!item.text.trim()).length;
}

/**
 * Maps a model's per-row messages onto the row keys the UI renders.
 *
 * @param rows - The model's own list, which the messages are index-aligned to.
 * @param messages - One entry per row, `undefined` where the row is clean.
 */
function byRowKey(
  rows: ReadonlyArray<{ key: string }>,
  messages: ReadonlyArray<string | undefined> | undefined,
): Record<string, string[]> {
  const byKey: Record<string, string[]> = {};
  rows.forEach((row, index) => {
    const message = messages?.[index];
    if (message) {
      byKey[row.key] = [message];
    }
  });
  return byKey;
}

/**
 * Sitelinks that repeat an earlier row's link text, keyed to the later row.
 *
 * Not a model constraint for the same reason as the keyword rule above — `array` owns the
 * per-row slot on `sitelinks`. The API compares link text case-insensitively.
 */
function getDuplicateSitelinks(
  sitelinks: readonly ISitelink[],
  t: TFunction,
): Record<string, string[]> {
  const firstSeen = new Map<string, number>();
  const byKey: Record<string, string[]> = {};

  sitelinks.forEach((sitelink, index) => {
    const text = sitelink.text.trim();
    if (!text) {
      return;
    }
    const identity = text.toLowerCase();
    const original = firstSeen.get(identity);
    if (original === undefined) {
      firstSeen.set(identity, index + 1);
    } else {
      byKey[sitelink.key] = [
        t('Repeats the link text of sitelink {{number}}.', { number: original }),
      ];
    }
  });

  return byKey;
}

function mergeRowErrors(
  first: Record<string, string[]>,
  second: Record<string, string[]>,
): Record<string, string[]> {
  const merged: Record<string, string[]> = { ...first };
  Object.entries(second).forEach(([key, messages]) => {
    merged[key] = [...(merged[key] ?? []), ...messages];
  });
  return merged;
}

/**
 * A message against every row left empty.
 *
 * Not a model constraint: `uniqueBy` has to stay the only per-row validator on these
 * attributes, and requiring presence there would stop an unfinished ad being saved at all.
 */
function getBlankRowErrors(
  items: ReadonlyArray<{ readonly key: string; readonly text: string }>,
  message: string,
): Record<string, string[]> {
  const byKey: Record<string, string[]> = {};
  items.forEach((item) => {
    if (!item.text.trim()) {
      byKey[item.key] = [message];
    }
  });
  return byKey;
}

function getAdRowErrors(ad: GoogleAdsSearchCreative, view: IAd, t: TFunction): IAdRowErrors {
  const errors = toJS(
    ad.validate(['destination_url', 'headlines', 'descriptions'], false, 'grouped').errors,
  ) as IAdModelErrors;

  /*
   * The final URL is required by Google but not by the endpoint, so it is reported here
   * rather than constrained on the creative — see the note on `GoogleAdsSearchCreative`.
   */
  const finalUrl = ad.get('destination_url', '').trim()
    ? errors.destination_url ?? []
    : [t('A final URL is required.')];

  return {
    headlines: mergeRowErrors(
      byRowKey(view.headlines, errors.headlines),
      getBlankRowErrors(view.headlines, t('Enter a headline or remove this line.')),
    ),
    descriptions: mergeRowErrors(
      byRowKey(view.descriptions, errors.descriptions),
      getBlankRowErrors(view.descriptions, t('Enter a description or remove this line.')),
    ),
    finalUrl,
  };
}

/**
 * The Ad Grant rules for one ad group with no field to sit against — the counts and
 * minimums. Everything with a home on a field comes from the models' constraints.
 */
function getAdGroupCountErrors(view: IAdGroup, t: TFunction): string[] {
  const errors: string[] = [];

  if (countFilled(view.keywords) < AD_GRANT_LIMITS.minKeywordsPerAdGroup) {
    errors.push(t('Add at least one keyword.'));
  }

  if (view.sitelinks.length < AD_GRANT_LIMITS.minSitelinksPerAdGroup) {
    errors.push(
      t('Add at least {{minimum}} sitelinks.', {
        minimum: AD_GRANT_LIMITS.minSitelinksPerAdGroup,
      }),
    );
  }
  if (view.sitelinks.length > MAX_SITELINKS_PER_AD_GROUP) {
    errors.push(
      t('Remove sitelinks — an ad group can have at most {{maximum}}.', {
        maximum: MAX_SITELINKS_PER_AD_GROUP,
      }),
    );
  }

  if (view.ads.length < AD_GRANT_LIMITS.minAdsPerAdGroup) {
    errors.push(t('Add at least {{minimum}} ads.', { minimum: AD_GRANT_LIMITS.minAdsPerAdGroup }));
  }

  /*
   * Counted off the view rather than constrained on the creative: the minimum counts the
   * rows that are filled in, and a blank row is a placeholder the editor seeds.
   */
  view.ads.forEach((ad, index) => {
    const adNumber = index + 1;
    if (countFilled(ad.headlines) < AD_TEXT_LIMITS.headline.minItems) {
      errors.push(
        t('Ad {{adNumber}} needs at least {{minimum}} headlines.', {
          adNumber,
          minimum: AD_TEXT_LIMITS.headline.minItems,
        }),
      );
    }
    if (countFilled(ad.descriptions) < AD_TEXT_LIMITS.description.minItems) {
      errors.push(
        t('Ad {{adNumber}} needs at least {{minimum}} descriptions.', {
          adNumber,
          minimum: AD_TEXT_LIMITS.description.minItems,
        }),
      );
    }
  });

  return errors;
}

/** Every message for one ad group, split into the ones with a field and the ones without. */
export function getAdGroupErrors(
  input: IAdGroupValidationInput,
  t: TFunction,
): { rowErrors: IAdGroupRowErrors; errors: string[] } {
  const { view, model, ads } = input;
  const modelErrors = toJS(
    model.validate(['name', 'keywords', 'sitelinks'], false, 'grouped').errors,
  ) as IAdGroupModelErrors;

  /*
   * Keyed off the model's own lists, which the messages are index-aligned to — the view
   * splits keywords across two editors and drops archived ads.
   */
  const sitelinkRows = model.get('sitelinks', []);
  const sitelinks: Record<string, ISitelinkRowErrors> = {};
  sitelinkRows.forEach((row, index) => {
    const error = modelErrors.sitelinks?.[index];
    if (error) {
      sitelinks[row.key] = { text: error.link_text ?? [], url: error.final_url ?? [] };
    }
  });
  Object.entries(getDuplicateSitelinks(view.sitelinks, t)).forEach(([key, messages]) => {
    const existing = sitelinks[key] ?? { text: [], url: [] };
    sitelinks[key] = { ...existing, text: [...existing.text, ...messages] };
  });

  const adErrors: Record<string, IAdRowErrors> = {};
  view.ads.forEach((adView) => {
    const ad = ads.find((candidate) => candidate.id === adView.key);
    if (ad) {
      adErrors[adView.key] = getAdRowErrors(ad, adView, t);
    }
  });

  /*
   * Both editors add rows before they hold anything, so a blank row is a line waiting to be
   * filled rather than a mistake about its contents. Its message replaces the rest: the
   * duplicate rule pairs two blank rows off against each other, and the single-word rule
   * counts a blank as one word.
   */
  const blank = getBlankRowErrors(
    [...view.keywords, ...view.negativeKeywords],
    t('Enter a keyword or remove this line.'),
  );

  const tooGeneric: Record<string, string[]> = {};
  view.keywords
    .filter((keyword) => !blank[keyword.key] && isKeywordTooGeneric(keyword.text))
    .forEach((keyword) => {
      tooGeneric[keyword.key] = [t('Too generic — Ad Grant policy blocks single-word keywords.')];
    });

  const keywords = mergeRowErrors(
    byRowKey(model.get('keywords', []), modelErrors.keywords),
    tooGeneric,
  );
  Object.entries(blank).forEach(([key, messages]) => {
    keywords[key] = messages;
  });

  return {
    rowErrors: {
      name: modelErrors.name ?? [],
      keywords,
      sitelinks,
      ads: adErrors,
    },
    errors: getAdGroupCountErrors(view, t),
  };
}

/** Whether an ad group has anything wrong with it, in either bucket. */
function hasErrors(rowErrors: IAdGroupRowErrors, errors: string[]): boolean {
  if (errors.length || rowErrors.name.length || Object.keys(rowErrors.keywords).length) {
    return true;
  }
  if (Object.values(rowErrors.sitelinks).some(({ text, url }) => !!text.length || !!url.length)) {
    return true;
  }
  return Object.values(rowErrors.ads).some(
    (ad) =>
      !!ad.finalUrl.length ||
      !!Object.keys(ad.headlines).length ||
      !!Object.keys(ad.descriptions).length,
  );
}

/**
 * Validates the Ads step: the models' own constraints, plus the Ad Grant rules that span
 * more than one model.
 *
 * @param inputs - One entry per ad group. See {@link IAdGroupValidationInput}.
 * @param t - Translation function, passed in so this stays pure and testable.
 */
export function validateAdStep(
  inputs: readonly IAdGroupValidationInput[],
  t: TFunction,
): IValidateAdStepResult {
  const results = inputs.map((input) => getAdGroupErrors(input, t));
  const rowErrors = results.map((result) => result.rowErrors);
  const adGroupErrors = results.map((result) => result.errors);

  /*
   * Two ad groups sharing a name is only visible from here — a model cannot see its
   * siblings. Reported against the later one so the message lands on the name just typed,
   * and compared case-insensitively, since two cards reading `Screenings` and `screenings`
   * are indistinguishable in the list.
   */
  const namesSeen = new Set<string>();
  inputs.forEach((input, index) => {
    const name = input.view.name.trim().toLowerCase();
    if (!name) {
      return;
    }
    if (namesSeen.has(name)) {
      rowErrors[index].name = [
        ...rowErrors[index].name,
        t('Another ad group is already called this.'),
      ];
    }
    namesSeen.add(name);
  });

  const invalidAdGroupIndexes = results.reduce<number[]>((indexes, result, index) => {
    if (hasErrors(result.rowErrors, result.errors)) {
      indexes.push(index);
    }
    return indexes;
  }, []);

  const adsStepErrors: string[] = [];
  if (inputs.length < AD_GRANT_LIMITS.minAdGroups) {
    adsStepErrors.push(
      t('Add at least {{minimum}} ad groups.', { minimum: AD_GRANT_LIMITS.minAdGroups }),
    );
  }
  invalidAdGroupIndexes.forEach((index) => {
    // Resolved before the outer t(): i18next-parser's lexer doesn't descend into a t()
    // nested in another t()'s options, so inlining this would skip the locale files.
    const name = inputs[index].view.name.trim() || t('Untitled ad group');
    adsStepErrors.push(t('"{{- name}}" must be completed.', { name }));
  });

  return { rowErrors, adGroupErrors, invalidAdGroupIndexes, adsStepErrors };
}
